import React, { useState, useEffect, ChangeEvent } from 'react';
import { searchService, EmailSearchParams, SearchResponse, EmailMessage } from '@/services/search';
import { Filter } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Pagination } from '@/components/ui/Pagination';
import { useDebounce } from '@/hooks/useDebounce';
import { EmailSearchFilters as EmailSearchFiltersComponent } from './EmailSearchFilters';
import LoadingState from '@/components/ui/LoadingState';
import EmptyState from '@/components/ui/EmptyState';
import { EmailListItem } from '@/components/EmailListItem';
import { useNavigate } from 'react-router-dom';

const EmailSearchPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<EmailMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<Omit<EmailSearchParams, 'query'>>({
    searchIn: 'all',
    hasAttachments: false,
    unread: false,
    folders: [],
    page: 1,
    limit: 20
  });
  const [pagination, setPagination] = useState<SearchResponse['pagination'] | null>(null);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleEmailClick = (email: EmailMessage) => {
    // Navigate to the EmailChatPage with the email data
    navigate(`/email/${email.folder}/${email.id}/${email.uid}`, {
      state: { emailInitial: email }
    });
  };

  const performSearch = async (query: string, filters: Omit<EmailSearchParams, 'query'>) => {
    if (!query.trim()) {
      setResults([]);
      setPagination(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const searchParams: EmailSearchParams = {
        query,
        ...filters
      };
      const response = await searchService.searchEmails(searchParams);
      
      // Convert SearchMessages to EmailMessage format
      const convertedMessages: EmailMessage[] = response.messages.map(msg => ({
        uid: parseInt(msg.uid),
        folder: msg.folderPath,
        id: msg.id,
        headers: {
          from: msg.headers.from,
          to: msg.headers.to,
          subject: msg.headers.subject,
          date: msg.headers.date
        },
        body: {
          text: msg.body,
          html: msg.html
        },
        flags: [],
        size: 0
      }));

      setResults(convertedMessages);
      setPagination(response.pagination);
    } catch (error) {
      // Don't show error for cancelled requests
      if (error instanceof Error && error.message === 'Search request cancelled') {
        return;
      }
      setError('Failed to search messages');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = (newFilters: Omit<EmailSearchParams, 'query'>) => {
    setFilters(newFilters);
  };

  const handlePageChange = (page: number) => {
    if (!debouncedSearchQuery.trim()) return;
    performSearch(debouncedSearchQuery, { ...filters, page });
  };

  useEffect(() => {
    if (!debouncedSearchQuery.trim()) {
      setResults([]);
      setPagination(null);
      return;
    }

    performSearch(debouncedSearchQuery, filters);

    // Cleanup function to cancel pending requests on unmount
    return () => {
      searchService.cancelAllSearchRequests();
    };
  }, [debouncedSearchQuery, filters]);

  return (
    <div className="flex flex-col h-full">
      {/* Search Header */}
      <div className="flex items-center gap-4 p-6 border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="flex-1 max-w-2xl">
          <Input
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search emails..."
            className="w-full"
            autoComplete="off"
            style={{
              fontWeight: 500,
              color: '#171725',
            }}
          />

          {/* add a button to clear the search */}
          <Button
            variant="outline"
            onClick={() => setSearchQuery('')}
            className="mt-2"
          >
            Clear Search</Button>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
        >
          <Filter size={16} />
          <span>Filters</span>
        </Button>
      </div>

      {/* Main Content */}
      <div className="flex-1 relative">
        {/* Results */}
        <div className="h-full overflow-auto">
          {loading ? (
            <LoadingState folderName="search" />
          ) : error ? (
            <EmptyState
              folderName="error"
              onCompose={() => {}}
            />
          ) : results.length === 0 ? (
            <EmptyState
              folderName="search"
              onCompose={() => {}}
            />
          ) : (
            <div className="divide-y divide-gray-200">
              {results.map((email) => (
                <EmailListItem
                  key={email.uid}
                  senderName={email.headers.from}
                  subject={email.headers.subject}
                  message={email.body.text || email.body.html}
                  timestamp={email.headers.date}
                  isRead={email.flags.includes('\\Seen')}
                  statusDotUrl={email.flags.includes('\\Seen') ? '/images/read.png' : '/images/unread.png'}
                  onClick={() => handleEmailClick(email)}
                  className="cursor-pointer hover:bg-gray-50"
                />
              ))}
            </div>
          )}
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="absolute right-0 top-0 h-full w-80 bg-white border-l border-gray-200 shadow-lg z-20">
            <EmailSearchFiltersComponent
              filters={{
                ...filters,
                query: debouncedSearchQuery
              }}
              onFiltersChange={handleFiltersChange}
              onClose={() => setShowFilters(false)}
            />
          </div>
        )}

        {/* Pagination */}
        {pagination && results.length > 0 && (
          <div className="p-4 border-t border-gray-200 bg-white sticky bottom-0">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
              showFirstLast={false}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailSearchPage; 